import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaRegClock, FaUserTie, FaUser, FaShuffle } from "react-icons/fa6";
import { IoMdSchool } from "react-icons/io";
import { TiTick } from "react-icons/ti";
import { ImCross } from "react-icons/im";
import styles from "./staffattendence.module.css";
import AxiosInstance, { TokenService } from "../../../services/Api";

const StaffAttendanceDashboard = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const clientId = clientData.client_id || "";
  const school_id = clientData?.school_id || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState({
    total_staff_count: 0,
    total_male_staff_count: 0,
    total_female_staff_count: 0,
    total_present_count: 0,
    total_male_present_count: 0,
    total_female_present_count: 0,
    total_absent_count: 0,
    total_male_absent_count: 0,
    total_female_absent_count: 0,
    staff_list: [],
  });

  const refreshTimerRef = useRef(null);
  const timeTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Format date for display with live updates
  const getFormattedDateTime = useCallback((date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    const formattedHours = hours % 12 || 12;

    return `${day}/${month}/${year} ${formattedHours}:${minutes}:${seconds} ${ampm}`;
  }, []);

  // Update clock every second
  useEffect(() => {
    timeTimerRef.current = setInterval(() => {
      if (isMountedRef.current) {
        setCurrentTime(new Date());
      }
    }, 1000);

    return () => {
      if (timeTimerRef.current) {
        clearInterval(timeTimerRef.current);
      }
    };
  }, []);

  const fetchAttendanceData = useCallback(async () => {
    if (!isMountedRef.current) return;
    setLoading(true);
    try {
      const response = await AxiosInstance.post(
        "/attendance/staff-attendance-dashboard",
        {
          client_id: clientId,
          school_id: school_id,
          date: getCurrentDate(),
        },
      );

      if (!isMountedRef.current) return;

      if (response.data?.status) {
        setAttendanceData(response.data.data);
        setError(null);
      } else {
        setError(
          response.data?.message ||
            translations?.labels?.fetchFailed ||
            "Invalid response format",
        );
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(
          err.message ||
            translations?.labels?.fetchFailed ||
            "Failed to fetch staff attendance data",
        );
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [clientId, school_id, translations]);

  useEffect(() => {
    isMountedRef.current = true;

    fetchAttendanceData();

    refreshTimerRef.current = setInterval(() => {
      fetchAttendanceData();
    }, 30000);

    return () => {
      isMountedRef.current = false;
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [fetchAttendanceData]);

  const StatCard = ({
    title,
    total,
    male,
    female,
    borderColor,
    icon,
    type,
  }) => (
    <div className="col-md-4 mb-4 px-2">
      <div
        className={`card h-100 ${styles.statCard} ${styles[`${type}Card`]}`}
        style={{ borderTop: `6px solid ${borderColor}` }}
      >
        <div className="card-body">
          <div className="d-flex justify-content-center align-items-center gap-2 mb-3">
            <h6 className={`text-uppercase fw-bold mb-0 ${styles.cardTitle}`}>
              {title}
            </h6>
            <span className={styles.cardIcon}>{icon}</span>
          </div>
        </div>

        <div className="card-body">
          <div className="row align-items-center text-center">
            <div className="col-5">
              <h2 className={`fw-bold mb-0 ${styles.totalNumber}`}>{total}</h2>
            </div>

            <div className="col-7">
              <div className={styles.genderStats}>
                <span className={styles.maleStat}>
                  <FaUserTie size={16} />
                  <span className="ms-1">{male}</span>
                </span>
                <span className={styles.divider}>|</span>
                <span className={styles.femaleStat}>
                  <FaUser size={16} />
                  <span className="ms-1">{female}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const StaffCard = ({ staff }) => {
    const isPresent = staff.attendance_status === "Present";
    const randomAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(
      staff.staff_name,
    )}&background=FFFFFF&color=000000`;
    const photoUrl = staff.profile_photo?.trim() || randomAvatar;

    return (
      <div
        className={styles.staffCardWrapper}
        onClick={() =>
          navigate(`/user/Staff-Attendance-Dashboard-List/${staff.staff_id}`, {
            state: { staffName: staff.staff_name },
          })
        }
      >
        <div
          className={`${styles.staffCard} ${
            isPresent ? styles.presentCard : styles.absentCard
          }`}
        >
          <div className={styles.staffCardTop}>
            <div className={styles.staffAvatar}>
              <img
                src={photoUrl}
                alt={staff.staff_name}
                className={styles.avatarImg}
              />
            </div>
            {isPresent && (
              <div className={styles.staffTimes}>
                {staff.sign_in_time && (
                  <div className={styles.timeIn}>
                    <FaRegClock size={14} />
                    <span>{staff.sign_in_time}</span>
                  </div>
                )}
                {staff.sign_out_time && (
                  <div className={styles.timeOut}>
                    <FaRegClock size={14} />
                    <span>{staff.sign_out_time}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className={styles.staffName}>
            {staff.staff_name
              .toLowerCase()
              .replace(/\b\w/g, (l) => l.toUpperCase())}
          </div>
          <div
            className={`${styles.statusBar} ${
              isPresent ? styles.presentBar : styles.absentBar
            }`}
          ></div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div
          className={`d-flex justify-content-center align-items-center ${styles.loadingContainer}`}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">
              {translations?.labels?.loading || "Loading..."}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">
            {translations?.labels?.error || "Error Loading Data"}
          </h4>
          <p>{error}</p>
          <hr />
          <p className="mb-0">
            {translations?.labels?.retry ||
              "Please try refreshing the page or contact support."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`container-fluid py-4 ${styles.dashboard}`}>
      {/* Header */}
      <div className="row mb-4 align-items-center">
        <div className="col-md-4">
          <h2 className={`fw-bold mb-0 ${styles.mainTitle}`}>
            {translations?.labels?.staffAttendanceDashboard ||
              "Staff Attendance Dashboard"}
          </h2>
        </div>

        <div className="col-md-8 d-flex justify-content-end align-items-center gap-3">
          <p className={`text-muted mb-0 ${styles.dateTime}`}>
            {getFormattedDateTime(currentTime)}
          </p>
          <button
            className={`btn btn-sm d-flex align-items-center gap-2 ${styles.switchButton}`}
            onClick={() => navigate("/user/Student-Attendance-Dashboard")}
          >
            <FaShuffle size={18} />
            <span>
              {translations?.labels?.switchToStudentDashboard ||
                "Switch to Student Dashboard"}
            </span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row">
        <StatCard
          title={translations?.labels?.total || "Total"}
          total={attendanceData.total_staff_count}
          male={attendanceData.total_male_staff_count}
          female={attendanceData.total_female_staff_count}
          borderColor="#0d6efd"
          type="blue"
          icon={<IoMdSchool color="#0d6efd" size={24} />}
        />

        <StatCard
          title={translations?.labels?.present || "Present"}
          total={attendanceData.total_present_count}
          male={attendanceData.total_male_present_count}
          female={attendanceData.total_female_present_count}
          borderColor="var(--color-success)"
          type="green"
          icon={<TiTick color="var(--color-success)" size={24} />}
        />

        <StatCard
          title={translations?.labels?.absent || "Absent"}
          total={attendanceData.total_absent_count}
          male={attendanceData.total_male_absent_count}
          female={attendanceData.total_female_absent_count}
          borderColor="var(--color-danger)"
          type="red"
          icon={<ImCross color="var(--color-danger)" size={20} />}
        />
      </div>

      {/* Staff Cards Grid */}
      <div className={styles.staffGrid}>
        {attendanceData.staff_list.map((staff) => (
          <StaffCard key={staff.staff_id} staff={staff} />
        ))}
      </div>
    </div>
  );
};

export default StaffAttendanceDashboard;
